import { Link } from "gatsby";
import React, { FC, MouseEvent, ReactNode } from "react";
import styled from "styled-components";
import RoundedRectangleImage from "./atoms/RoundedRectangleImage";
import { mainURL } from "../constants";
import { FaHistory } from "react-icons/fa";

const Container = styled.div<{ $guarded: boolean }>`
  max-width: 960px;
  margin: 0 auto;
  padding: 8px;
  font-family: -apple-system, Roboto, sans-serif, serif;
  user-select: ${({ $guarded }) => ($guarded ? `none` : `auto`)};
  @media print {
    display: none;
  }
`;
const Header = styled.header`
  display: flex;
  align-items: center;
  height: 60px;
`;
const TopLink = styled(Link)`
  color: black;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.2em;
  margin-right: 12px;
  border-radius: 4px;
  padding: 8px 0;
  &:hover {
    background-color: #eee;
  }
`;
const HeaderLink = styled(Link)`
  font-size: 14px;
  padding: 8px 12px;
  color: black;
  text-decoration: none;
  border-radius: 4px;
  &:hover {
    background-color: #eee;
  }
`;
const HeaderLinkIcon = styled(Link)`
  display: flex;
  font-size: 20px;
  margin: 0 4px;
  padding: 8px 12px;
  color: black;
  border-radius: 4px;
  &:hover {
    background-color: #eee;
  }
`;
const Footer = styled.footer`
  margin-top: 200px;
  display: flex;
  flex-direction: column;
  space: 5px;
  justify-content: space-between;
  border-top: solid thin black;
  padding-top: 12px;
`;
const SNSLinksContainer = styled.div`
  display: flex;
`;
const SNSLink = styled.a`
  margin: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const RequestLink = styled.a`
  margin: 4px 0;
  padding: 8px;
`;
const FooterLink = styled(Link)`
  margin: 4px 0;
  padding: 8px;
`;
const LicenseContainer = styled.div`
  padding: 8px;
  font-size: 12px;
  color: #555;
  display: flex;
  align-items: center;
`;
const LicenseImg = styled.img`
  margin-right: 8px;
`;
const LicenseP = styled.p`
  margin: 0;
`;
const Copyright = styled.div`
  margin: 4px 0 4px auto;
  padding: 8px;
`;

interface Props {
  isClickDisabled?: boolean;
  isSearchVisible?: boolean;
  isAboutVisible?: boolean;
  withDomain?: boolean;
  siteUrl?: string;
  children: ReactNode;
}

const Layout: FC<Props> = ({
  isClickDisabled = false,
  isSearchVisible = false,
  isAboutVisible = false,
  withDomain = false,
  siteUrl = "",
  children,
}) => {
  const handleContextMenu = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const prePath = withDomain ? siteUrl : "";

  return (
    <Container
      onContextMenu={isClickDisabled ? handleContextMenu : undefined}
      $guarded={isClickDisabled}
    >
      <div itemScope itemType="https://schema.org/WebSite">
        <link itemProp="url" href={mainURL} />
        <meta itemProp="name" content="コード進行メモ倉庫" />
      </div>
      <Header>
        <TopLink to={`${prePath}/`}>コード進行メモ倉庫</TopLink>
        {isSearchVisible && <HeaderLink to="/">Search</HeaderLink>}
        {isAboutVisible && <HeaderLink to="/">About</HeaderLink>}
        <HeaderLinkIcon to="/history/">
          <FaHistory />
        </HeaderLinkIcon>
      </Header>
      {children}
      <Footer>
        <SNSLinksContainer>
          <SNSLink
            href="https://tiktok.com/@chords.repo"
            rel="noreferrer"
            target="_blank"
          >
            <RoundedRectangleImage
              src="/sns/tiktok.svg"
              alt="TikTok"
              width="32"
              height="32"
            />
          </SNSLink>
          <SNSLink
            href="https://youtube.com/@コード進行メモ倉庫"
            rel="noreferrer"
            target="_blank"
          >
            <img
              src="/sns/youtube.png"
              alt="YouTube"
              width={24}
              height="auto"
            />
          </SNSLink>
          <SNSLink
            href="https://instagram.com/chords.repo"
            rel="noreferrer"
            target="_blank"
          >
            <img
              src="/sns/instagram.png"
              alt="Instagram"
              width="32"
              height="32"
            />
          </SNSLink>
        </SNSLinksContainer>
        <RequestLink
          href="https://docs.google.com/forms/d/e/1FAIpQLSfH1yfrhbWT8byuhbbZVbYQ7ie7wNaqWr2viTxiVa7I52fP0w/viewform?usp=sf_link"
          rel="noreferrer"
          target="_blank"
        >
          リクエストフォーム
        </RequestLink>
        <FooterLink to={`${prePath}/terms_of_service/`}>利用規約</FooterLink>
        <FooterLink to={`${prePath}/privacy_policy/`}>
          プライバシーポリシー
        </FooterLink>
        <LicenseContainer>
          <LicenseImg
            src="/jasrac.JPG"
            alt="JASRAC許諾マーク"
            width={60}
            height={60}
          />
          <div>
            <LicenseP>JASRAC許諾番号：</LicenseP>
            <LicenseP>JASRAC許諾第9040419001Y38026号</LicenseP>
          </div>
        </LicenseContainer>
        <LicenseContainer>
          <LicenseImg
            src="/nextone.png"
            alt="NexTone許諾マーク"
            width={60}
            height={60}
          />
          <div>
            <LicenseP>NexTone許諾番号：</LicenseP>
            <LicenseP>ID000009986</LicenseP>
          </div>
        </LicenseContainer>
        <Copyright>©コード進行メモ倉庫</Copyright>
      </Footer>
    </Container>
  );
};

export default Layout;
